/**
 * FR-018.7 §8 — the thin vocabulary-term page for crops.
 *
 * `/farm?lense=crop_cycles&term=<canonical>`: an ObjectView-shaped page for a term that has no
 * twin entity of its own. It shows what the farmer needs to answer "which spellings map to
 * Erdbeere, and which crop cycles carry it?" — the Herkunft section (the Sources rows of the
 * term, with the same editor intents as any object) and the crop cycles whose `crop` equals it.
 * The mapping editor and group panel are hosted by the page, exactly as for an object.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SourceRowDto } from '../../../../services/sourcesService';
import { twinService } from '../../../../services/twinService';
import { useCropTerms } from '../../../../hooks/useSources';
import SourcesPanel, { type EditorIntent } from './SourcesPanel';
import '../ObjectView.css';
import './TermPage.css';

/** Cycles shown per term; a term carried by more says so instead of paging. */
const CYCLE_LIMIT = 200;

/** The same primary_key -> entity_id -> id fallback as FarmPage's `resolveEntityRowId`. */
function rowId(row: Record<string, unknown>, primaryKey?: string | null): string {
    for (const v of [primaryKey ? row[primaryKey] : undefined, row.entity_id, row.id]) {
        if (v != null && v !== '') return String(v);
    }
    return '';
}

function yearOf(row: Record<string, unknown>): number {
    const y = Number(row.crop_year);
    return row.crop_year == null || row.crop_year === '' || Number.isNaN(y) ? -1e9 : y;
}

type T = (key: string, def: string, opts?: Record<string, unknown>) => string;

export interface TermPageProps {
    term: string;
    onBack(): void;
    onNavigate(entityType: string, entityId: string): void;
    onOpenEditor(intent: EditorIntent): void;
    onOpenGroup(row: SourceRowDto): void;
    onPreviewDocument?(docId: string): void;
}

export const TermPage: React.FC<TermPageProps> = ({
    term,
    onBack,
    onNavigate,
    onOpenEditor,
    onOpenGroup,
    onPreviewDocument,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as unknown as T;
    const { data: terms } = useCropTerms();
    const counts = terms?.find((c) => c.term === term);
    const cycles = useQuery({
        queryKey: ['twin', 'sources', 'term-cycles', 'crop', term],
        queryFn: () => twinService.listEntities('crop_cycle', { crop: term }, { limit: CYCLE_LIMIT }),
    });

    const countParts = counts
        ? [
              t('farm.sources.term.counts.cycles', '{{n}} Kulturen', { count: counts.cycles, n: counts.cycles }),
              t('farm.sources.term.counts.mappings', '{{n}} Zuordnungen', { count: counts.mappings, n: counts.mappings }),
          ]
        : [];
    // Newest crop year first; a cycle without a year goes last. The read's order is unspecified.
    const rows = [...(cycles.data?.entities ?? [])].sort((a, b) => yearOf(b) - yearOf(a));

    return (
        <div className="object-view term-page">
            <button type="button" className="object-view__back" onClick={onBack}>
                <ArrowLeft size={14} />
                {t('farm.sources.term.back', 'Zurück zu Kulturen')}
            </button>

            <header className="object-view__header">
                <div className="object-view__heading">
                    <h2 className="object-view__title">{term}</h2>
                    <span className="object-view__type">{t('farm.sources.term.title', 'Kultur')}</span>
                    {countParts.length > 0 && <span className="term-page__counts">{countParts.join(' · ')}</span>}
                </div>
            </header>

            <div className="object-view__grid">
                <div className="object-view__main">
                    <section className="object-view__section object-view__section--sources">
                        <h3 className="object-view__section-title">{t('farm.sources.title', 'Herkunft')}</h3>
                        <SourcesPanel
                            entityType="crop"
                            entityId={term}
                            onOpenEditor={onOpenEditor}
                            onOpenGroup={onOpenGroup}
                            onNavigate={onNavigate}
                            onPreviewDocument={onPreviewDocument}
                        />
                    </section>
                </div>

                <div className="object-view__side">
                    <section className="object-view__section">
                        <h3 className="object-view__section-title">
                            {t('farm.sources.term.cyclesTitle', 'Kulturen mit diesem Begriff')}
                        </h3>
                        {cycles.isLoading ? (
                            <p className="object-view__loading">{t('farm.sources.term.loading', 'Wird geladen…')}</p>
                        ) : cycles.isError ? (
                            <p className="object-view__missing">
                                {t('farm.sources.term.cyclesError', 'Die Kulturen sind gerade nicht verfügbar.')}
                            </p>
                        ) : rows.length === 0 ? (
                            <p className="object-view__missing">
                                {t('farm.sources.term.noCycles', 'Keine Kulturen mit diesem Begriff.')}
                            </p>
                        ) : (
                            <ul className="term-page__cycles">
                                {rows.map((row) => {
                                    const id = rowId(row, cycles.data?.primary_key);
                                    const name = typeof row.name === 'string' && row.name.trim() ? row.name.trim() : term;
                                    const year = row.crop_year;
                                    return (
                                        <li key={id}>
                                            <button
                                                type="button"
                                                className="term-page__cycle"
                                                onClick={() => onNavigate('crop_cycle', id)}
                                            >
                                                {name}
                                                {year != null && year !== '' && (
                                                    <>
                                                        {' '}
                                                        <span className="term-page__year">{String(year)}</span>
                                                    </>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {(cycles.data?.total ?? 0) > rows.length && (
                            <p className="term-page__more">
                                {t('farm.sources.term.more', '{{n}} weitere nicht angezeigt', {
                                    n: (cycles.data?.total ?? 0) - rows.length,
                                })}
                            </p>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermPage;
